'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import ShoppingCart from '~/features/cart/ShoppingCart';
import Wishlist_NavbarButton from '~/features/wishlist/navbar-button';
import LocaleSelector from '~/features/i18n/LanguageSelect/LocaleSelector';
import { useStateObject } from '~/utils/react';
import { SearchModalButton } from '~/features/activities/SearchModal';
import type { SB_HeaderProps } from '~/storyblok/components/types.generated';
import type { action_getFeaturedActivityCategories } from '~/app/[locale]/(web)/_data/actions';
import { transformStoryblokLink } from '~/storyblok/components/utils';
import { cx } from 'class-variance-authority';
import { NavigationMenu, NavigationMenuList, navigationMenuTriggerPillStyle } from '~/components/primitives/NavigationMenu';
import { Icon } from '~/components/primitives';
import WebHeaderMenuItem from './WebHeaderMenuItem';
import AccountButton from '~/features/auth/user/AccountButton';
import { PortalSwitcher } from '~/features/portals/switcher';
import { Auth } from '~/modules/auth/client';
import WebMobileNav from './WebMobileNav';
import LogoAnimated from './LogoAnimated';
import { useI18n } from '~/features/i18n';
import { useLayoutEffect } from 'react';
import { I18N_CONF } from '@funkey/locales/config';
import { useNavbarScrollOffset } from '~/features/layout/Navbar';
import useScrollPosition from '~/components/primitives/hooks/useScrollPosition';
import { usePlpExperiment } from '~/features/activities/search/plpExperiment';
import { UspTopBar, USP_TOP_BAR_HEIGHT_CLASS, USP_TOP_BAR_HEIGHT_PX, type UspTopBarData } from './UspTopBar';
const FADE_IN_THRESHOLD = 200;
export const WebHeaderClient: React.FC<{
  categories: Awaited<ReturnType<typeof action_getFeaturedActivityCategories>>;
  story: SB_HeaderProps;
  usps: UspTopBarData;
}> = props => {
  const $ = _c(76);
  const navRef = useNavbarScrollOffset();
  const isOpenSidebar = useStateObject(false);
  const position = useScrollPosition();
  const hasBackground = position > 20;
  const i18n = useI18n();
  const auth = Auth.useSession();
  const hasUspBar = usePlpExperiment();
  const uspBarOffset = hasUspBar ? Math.min(position, USP_TOP_BAR_HEIGHT_PX) : 0;
  let t0;
  let t1;
  if ($[0] !== hasUspBar || $[1] !== uspBarOffset) {
    t0 = () => {
      if (hasUspBar) {
        document.documentElement.dataset.uspBar = "";
      } else {
        delete document.documentElement.dataset.uspBar;
      }
      document.documentElement.style.setProperty("--funkey-usp-bar-offset", `${uspBarOffset}px`);
    };
    t1 = [hasUspBar, uspBarOffset];
    $[0] = hasUspBar;
    $[1] = uspBarOffset;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useLayoutEffect(t0, t1);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cx("fixed right-[calc(var(--removed-body-scroll-bar-size)/2)] z-3 flex h-header w-full flex-col transition-height duration-300");
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = -uspBarOffset;
  let t4;
  if ($[5] !== t3) {
    t4 = {
      top: t3
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== hasUspBar || $[8] !== props.usps) {
    t5 = hasUspBar && <div className={cx("shrink-0 bg-blue-950", USP_TOP_BAR_HEIGHT_CLASS.bar)}><UspTopBar {...props.usps} /></div>;
    $[7] = hasUspBar;
    $[8] = props.usps;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = hasBackground && "opacity-100";
  let t7;
  if ($[10] !== t6) {
    t7 = cx("header-rounding duration-50 pointer-events-none absolute inset-0 z-2 bg-primary opacity-0 transition-opacity", t6);
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = hasBackground ? position / FADE_IN_THRESHOLD : 0;
  let t9;
  if ($[12] !== t8) {
    t9 = {
      opacity: t8
    };
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t7 || $[15] !== t9) {
    t10 = <div className={t7} style={t9} />;
    $[14] = t7;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== i18n.locale) {
    t11 = I18N_CONF.getLocalePathPrefix(i18n.locale) || "/";
    $[17] = i18n.locale;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <LogoAnimated className="h-full w-full" />;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t11) {
    t13 = <Link href={t11} className="h-16 w-20 min-w-[70px] md:-ml-10 md:w-auto lg:ml-0 lg:mr-6 2xl:mr-12">{t12}</Link>;
    $[20] = t11;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== i18n.locale || $[23] !== props.categories || $[24] !== props.story.menuItems) {
    t14 = props.story.menuItems?.map(menuItem => <WebHeaderMenuItem key={menuItem._uid} locale={i18n.locale} menuItem={menuItem} categories={props.categories}>{"text" in menuItem ? menuItem.text : null}</WebHeaderMenuItem>);
    $[22] = i18n.locale;
    $[23] = props.categories;
    $[24] = props.story.menuItems;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t14) {
    t15 = <NavigationMenu><NavigationMenuList className="hidden min-h-16 gap-1 lg:flex">{t14}</NavigationMenuList></NavigationMenu>;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== i18n.locale || $[29] !== props.story.ctaItems) {
    t16 = props.story.ctaItems?.map(ctaItem => {
      if (!ctaItem.link) {
        return null;
      }
      const link = transformStoryblokLink(ctaItem.link, i18n.locale);
      return <Link {...link} className={navigationMenuTriggerPillStyle({
        size: "lg",
        variant: "default"
      })} key={ctaItem.text}>{ctaItem.text}</Link>;
    });
    $[28] = i18n.locale;
    $[29] = props.story.ctaItems;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t16) {
    t17 = <div className="hidden items-center gap-5 xl:flex">{t16}</div>;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  let t19;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <LocaleSelector className="hidden lg:flex" />;
    t19 = <SearchModalButton />;
    $[33] = t18;
    $[34] = t19;
  } else {
    t18 = $[33];
    t19 = $[34];
  }
  let t20;
  if ($[35] !== auth.user || $[36] !== i18n.locale) {
    t20 = auth.user ? <PortalSwitcher locale={i18n.locale} /> : <AccountButton />;
    $[35] = auth.user;
    $[36] = i18n.locale;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Wishlist_NavbarButton />;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== i18n.locale) {
    t22 = I18N_CONF.getTranslatedUrl("cart", {
      locale: i18n.locale
    });
    $[39] = i18n.locale;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t22) {
    t23 = <ShoppingCart cartURL={t22} />;
    $[41] = t22;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== isOpenSidebar) {
    t24 = () => isOpenSidebar.set(true);
    $[43] = isOpenSidebar;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  let t26;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <span className="sr-only">Open sidebar</span>;
    t26 = <Icon name="hamburger-menu" strokeWidth="2" aria-hidden="true" />;
    $[45] = t25;
    $[46] = t26;
  } else {
    t25 = $[45];
    t26 = $[46];
  }
  let t27;
  if ($[47] !== t24) {
    t27 = <button type="button" className="-m-2.5 p-2.5 text-white lg:hidden" onClick={t24}>{t25}{t26}</button>;
    $[47] = t24;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== t17 || $[50] !== t20 || $[51] !== t23 || $[52] !== t27) {
    t28 = <nav className="flex items-center gap-4 md:gap-5">{t17}{t18}{t19}{t20}{t21}{t23}{t27}</nav>;
    $[49] = t17;
    $[50] = t20;
    $[51] = t23;
    $[52] = t27;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  let t29;
  if ($[54] !== t15 || $[55] !== t28) {
    t29 = <div className="flex items-center gap-4 text-white md:gap-4 lg:gap-6 xl:gap-8 2xl:gap-16">{t15}{t28}</div>;
    $[54] = t15;
    $[55] = t28;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== isOpenSidebar) {
    t30 = () => isOpenSidebar.set(false);
    $[57] = isOpenSidebar;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== isOpenSidebar.value || $[60] !== props.categories || $[61] !== props.story || $[62] !== t30) {
    t31 = <WebMobileNav isOpen={isOpenSidebar.value} onClose={t30} story={props.story} featuredCategories={props.categories} />;
    $[59] = isOpenSidebar.value;
    $[60] = props.categories;
    $[61] = props.story;
    $[62] = t30;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== t13 || $[65] !== t29 || $[66] !== t31) {
    t32 = <div className="container absolute inset-0 z-20 flex w-full items-center justify-between md:px-8" aria-label="Top">{t13}{t29}{t31}</div>;
    $[64] = t13;
    $[65] = t29;
    $[66] = t31;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  let t33;
  if ($[68] !== t10 || $[69] !== t32) {
    t33 = <div className="relative flex-1">{t10}{t32}</div>;
    $[68] = t10;
    $[69] = t32;
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  let t34;
  if ($[71] !== navRef || $[72] !== t33 || $[73] !== t4 || $[74] !== t5) {
    t34 = <header className="relative z-3 h-header"><div ref={navRef} className={t2} style={t4}>{t5}{t33}</div></header>;
    $[71] = navRef;
    $[72] = t33;
    $[73] = t4;
    $[74] = t5;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  return t34;
};